import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle } from 'lucide-react';
import { useHubSpotCallbackMutation } from '../../store/promoterApi';

/**
 * Exchanges the HubSpot authorization code exactly once, then returns to the wizard, which
 * restores the step the user left from. Kept in a hook rather than the component so the page
 * body stays effect-free.
 */
const useHubSpotCodeExchange = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [hubSpotCallback] = useHubSpotCallbackMutation();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    if (!code || !state) {
      setError(t('crmImport.hubspotCallback.noCodeError'));
      return;
    }

    const exchangeCode = async () => {
      try {
        await hubSpotCallback({ code, state }).unwrap();
        navigate('/crm-import?hubspot=connected', { replace: true });
      } catch (err: any) {
        setError(err?.data?.message || t('crmImport.hubspotCallback.connectFailed'));
      }
    };

    exchangeCode();
  }, [searchParams, hubSpotCallback, navigate, t]);

  return error;
};

const HubSpotCallback: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const error = useHubSpotCodeExchange();

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-balance">
            {t('crmImport.hubspotCallback.connectionFailed')}
          </h2>
          <p className="text-gray-600 mb-6 text-pretty">{error}</p>
          <button
            onClick={() => navigate('/crm-import', { replace: true })}
            className="px-6 py-3 bg-[#4339A0] text-white rounded-lg hover:bg-[#3d2f8f] active:scale-[0.96] transition-[background-color,transform] duration-200"
          >
            {t('crmImport.hubspotCallback.backToImport')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#4339A0] animate-spin mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2 text-balance">
          {t('crmImport.hubspotCallback.connecting')}
        </h2>
        <p className="text-gray-600 text-pretty">{t('crmImport.hubspotCallback.pleaseWait')}</p>
      </div>
    </div>
  );
};

export default HubSpotCallback;
